import React, { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { Check, X, Loader2, Mic2, ChevronLeft, ChevronRight, Inbox, CheckCircle2, XCircle } from "lucide-react";
import AdminLayout from "../components/admin/AdminLayout";
import { usePublicAuth } from "../context/PublicAuthContext";
import api from "../utils/api";

const STATUS_COLORS = {
  pending:  { bg: "bg-amber-500/10",   text: "text-amber-400",  label: "Ausstehend" },
  approved: { bg: "bg-emerald-500/10", text: "text-emerald-400", label: "Angenommen" },
  rejected: { bg: "bg-red-500/10",     text: "text-red-400",    label: "Abgelehnt" },
};

export default function AdminArtistApplicationsPage() {
  const { publicApi, publicUser } = usePublicAuth();
  const [apps, setApps] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [page, setPage] = useState(1);
  const [actionState, setActionState] = useState({}); // id → { loading, note, artistId, artistSearch, artistResults }
  const LIMIT = 20;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await publicApi("get", `/artist-applications/admin?status=${statusFilter}&page=${page}&limit=${LIMIT}`);
      const data = res.data;
      if (Array.isArray(data)) {
        setApps(data);
        setTotal(data.length);
      } else {
        setApps(data.items || []);
        setTotal(data.total || 0);
      }
    } catch { setApps([]); } finally { setLoading(false); }
  }, [publicApi, statusFilter, page]);

  useEffect(() => { load(); }, [load]);

  const searchArtist = async (appId, query) => {
    setActionState(p => ({ ...p, [appId]: { ...p[appId], artistSearch: query } }));
    if (!query?.trim()) { setActionState(p => ({ ...p, [appId]: { ...p[appId], artistResults: [] } })); return; }
    try {
      const res = await publicApi("get", `/artists/?search=${encodeURIComponent(query)}&limit=8`);
      setActionState(p => ({ ...p, [appId]: { ...p[appId], artistResults: res.data?.items || res.data?.artists || [] } }));
    } catch {}
  };

  const handleReview = async (appId, status) => {
    const state = actionState[appId] || {};
    setActionState(p => ({ ...p, [appId]: { ...state, loading: true } }));
    try {
      await publicApi("put", `/artist-applications/admin/${appId}`, {
        status,
        reviewer_note: state.note || null,
        artist_id: state.artistId || null,
      });
      setApps(prev => prev.filter(a => a.id !== appId));
      setTotal(t => t - 1);
    } catch (e) {
      alert(e?.response?.data?.detail || "Fehler");
    } finally {
      setActionState(p => ({ ...p, [appId]: { ...state, loading: false } }));
    }
  };

  const pages = Math.ceil(total / LIMIT) || 1;

  if (publicUser?.role !== "developer") return null;

  return (
    <AdminLayout>
      <Helmet><title>Artist-Bewerbungen | Admin</title></Helmet>
      <div className="p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-black text-white font-heading">Artist-Bewerbungen</h1>
            <p className="text-zinc-500 text-sm mt-0.5">Eingehende Artist-Profilverknüpfungsanfragen</p>
          </div>
          <div className="flex gap-1 bg-zinc-900 rounded-xl p-1">
            {["pending", "approved", "rejected"].map(s => (
              <button key={s} onClick={() => { setStatusFilter(s); setPage(1); }}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition capitalize ${
                  statusFilter === s ? "bg-rose-600 text-white" : "text-zinc-500 hover:text-white"
                }`}>
                {s === "pending" ? "Ausstehend" : s === "approved" ? "Angenommen" : "Abgelehnt"}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 size={20} className="animate-spin text-zinc-600" /></div>
        ) : apps.length === 0 ? (
          <div className="text-center py-20">
            <Inbox size={36} className="text-zinc-800 mx-auto mb-3" />
            <p className="text-zinc-500 text-sm">Keine Bewerbungen</p>
          </div>
        ) : (
          <div className="bg-zinc-900 border border-white/5 rounded-xl overflow-hidden">
            <div className="divide-y divide-white/5">
            {apps.map(app => {
              const sc = STATUS_COLORS[app.status] || STATUS_COLORS.pending;
              const state = actionState[app.id] || {};
              return (
                <div key={app.id} className="p-4 hover:bg-white/[0.02] transition-colors">
                  <div className="flex items-start gap-4">
                    {/* Avatar */}
                    <div className="w-10 h-10 rounded-xl bg-zinc-800 flex items-center justify-center flex-shrink-0">
                      <Mic2 size={16} className="text-zinc-600" />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-white font-bold text-sm">{app.username || app.email || "—"}</p>
                          {app.contact_email && (
                            <p className="text-zinc-400 text-xs mt-0.5 flex items-center gap-1">
                              <span className="text-zinc-600">Kontakt:</span> {app.contact_email}
                            </p>
                          )}
                          {app.artist_name && (
                            <p className="text-zinc-400 text-sm mt-0.5">Artist: <span className="text-rose-300">{app.artist_name}</span></p>
                          )}
                          {app.info_text && (
                            <p className="text-zinc-500 text-xs mt-1.5 italic max-w-md leading-relaxed">"{app.info_text}"</p>
                          )}
                          <p className="text-zinc-700 text-xs mt-1">
                            {app.created_at ? new Date(app.created_at).toLocaleDateString("de") : ""}
                          </p>
                        </div>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold flex-shrink-0 ${sc.bg} ${sc.text}`}>
                          {sc.label}
                        </span>
                      </div>

                      {/* Nur bei pending: Artist-Zuordnung + Actions */}
                      {app.status === "pending" && (
                        <div className="mt-3 space-y-2">
                          {/* Artist-Suche */}
                          <div className="relative">
                            <input
                              value={state.artistSearch || ""}
                              onChange={e => searchArtist(app.id, e.target.value)}
                              placeholder="Artist in DB suchen und verknüpfen…"
                              className="w-full bg-zinc-800 text-white rounded-xl px-3 py-2 text-xs outline-none ring-1 ring-inset ring-white/[0.06] focus:ring-rose-500/40 transition placeholder:text-zinc-600 max-w-sm"
                            />
                            {(state.artistResults?.length > 0) && (
                              <div className="absolute top-full mt-1 left-0 w-80 z-20 bg-zinc-950 border border-white/10 rounded-xl shadow-xl">
                                {state.artistResults.map(a => (
                                  <button key={a.id} onClick={() => {
                                    setActionState(p => ({ ...p, [app.id]: { ...p[app.id], artistId: a.id, artistSearch: a.name, artistResults: [] } }));
                                  }} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-white/5 transition text-left">
                                    {a.bild_url && <img src={a.bild_url} alt={a.name} className="w-6 h-6 rounded-md object-cover" />}
                                    <span className={`text-xs ${state.artistId === a.id ? "text-rose-400 font-bold" : "text-zinc-300"}`}>{a.name}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                          {state.artistId && (
                            <p className="text-emerald-400 text-xs flex items-center gap-1">
                              <Check size={10} /> Artist verknüpft
                            </p>
                          )}
                          {/* Notiz */}
                          <input
                            value={state.note || ""}
                            onChange={e => setActionState(p => ({ ...p, [app.id]: { ...p[app.id], note: e.target.value } }))}
                            placeholder="Notiz (optional)"
                            className="w-full bg-zinc-800 text-white rounded-xl px-3 py-2 text-xs outline-none ring-1 ring-inset ring-white/[0.06] max-w-sm"
                          />
                          {/* Buttons */}
                          <div className="flex gap-2">
                            <button onClick={() => handleReview(app.id, "approved")}
                              disabled={state.loading}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-emerald-600/15 text-emerald-400 hover:bg-emerald-600/25 text-xs font-bold transition disabled:opacity-50">
                              {state.loading ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                              Annehmen
                            </button>
                            <button onClick={() => handleReview(app.id, "rejected")}
                              disabled={state.loading}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-red-600/10 text-red-400 hover:bg-red-600/20 text-xs font-bold transition disabled:opacity-50">
                              <XCircle size={12} /> Ablehnen
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            </div>
          </div>
        )}

        {pages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="p-2 rounded-lg bg-zinc-900 text-zinc-400 hover:text-white disabled:opacity-40 transition">
              <ChevronLeft size={16} />
            </button>
            <span className="text-zinc-500 text-sm">{page} / {pages}</span>
            <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages}
              className="p-2 rounded-lg bg-zinc-900 text-zinc-400 hover:text-white disabled:opacity-40 transition">
              <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
